/**
 * Component to display a hierarchy of nested standards within a set as
 * list of accordion items.
 *
 * Enabling the user to select and navigate standards by expanding nested
 * display. Note, standards rendering is only done up to one level of nesting.
 */
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {
    Accordion,
    AccordionSection,
} from "@khanacademy/wonder-blocks-accordion";
import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {usePrefersReducedMotion} from "@repo/data-prefers-reduced-motion";

import {subTreeRootedAtStandardId} from "./helpers/transform-set-of-standards-to-tree.ts";
import type {Standard} from "./types.ts";

type Props = {
    // A custom header to render above the standards list.
    header: React.ReactNode;
    // The standards to display.
    standards: Array<Standard>;
    // The selected standard id.
    selectedId: string;
    // The callback to execute when a standard is selected
    onSelectStandard?: () => void;
};

export function NestedStandardsList({
    header,
    standards,
    selectedId,
    onSelectStandard,
}: Props): React.ReactElement {
    const prefersReducedMotion = usePrefersReducedMotion();

    const items = React.useMemo(() => {
        return standards.map((standard, i) => {
            const children = standard.standards || [];
            const cornerKind = i === 0 ? "square" : undefined;

            const sectionHeader = (
                <LabelSmall style={styles.sectionHeader}>
                    {standard.description}
                </LabelSmall>
            );
            return (
                <AccordionSection
                    header={sectionHeader}
                    key={standard.standardId}
                    cornerKind={cornerKind}
                    style={styles.accordionSection}
                >
                    <ul>
                        {children.map((child) => (
                            <ChildStandardItem
                                key={child.standardId}
                                standard={child}
                                isSelected={child.standardId === selectedId}
                                onSelectStandard={onSelectStandard}
                            />
                        ))}
                    </ul>
                </AccordionSection>
            );
        });
    }, [standards, selectedId, onSelectStandard]);

    // Figure out which accordion section corresponds to the currently
    // selected standard so we can expand it by default.
    let initiallyExpandedIndex: number | undefined = undefined;
    for (let i = 0; i < standards.length; i++) {
        const standard = standards[i];
        // TODO(starin): This is a bit of a hack to get if the ID is in
        // the subtree. It might be better to have the parent accessible
        // (or at least the parent ID of the selected standard)
        if (subTreeRootedAtStandardId(selectedId, standard)) {
            initiallyExpandedIndex = i;
            break;
        }
    }

    return (
        <View style={styles.container}>
            <View style={styles.header}>{header}</View>
            <Accordion
                initialExpandedIndex={initiallyExpandedIndex}
                animated={!prefersReducedMotion}
            >
                {items}
            </Accordion>
        </View>
    );
}

// For very large descriptions, we'll want to truncate and show a shorter form.
// This is needed, at least, for Texas which has very large descriptions.
// We'll prefix the standard ID as well.
const MAX_DESCRIPTION_LENGTH = 60;
const TX_MATH = "TEKS.Math";
function getDescription(
    setId: string,
    standardId: string,
    description: string,
): string {
    if (setId !== TX_MATH || description.length <= MAX_DESCRIPTION_LENGTH) {
        return description;
    }

    // split the description and only return the first sentance
    const sentences = description.split(".");
    return `${standardId}: ${sentences[0]}`;
}

function ChildStandardItem({
    standard,
    isSelected,
    onSelectStandard,
}: {
    standard: Standard;
    isSelected: boolean;
    onSelectStandard?: () => void;
}): React.ReactElement {
    const setId = standard.setId || "";
    const href = `/standards/${setId}/${standard.standardId}`;
    return (
        <li
            className={css(
                styles.childListItem,
                isSelected && styles.childListItemSelected,
            )}
        >
            <Link
                href={href}
                className={css(
                    styles.childListItemLink,
                    isSelected && styles.childListItemLinkSelected,
                )}
                onClick={onSelectStandard}
            >
                {getDescription(
                    standard.setId ?? "",
                    standard.standardId,
                    standard.description,
                )}
            </Link>
        </li>
    );
}

const styles = StyleSheet.create({
    container: {
        overflowY: "auto",
        // Make space for the outline so that it doesn't get cut off
        // by the scroll overflow.
        padding: 1,
    },

    accordionSection: {
        backgroundColor: color.white,
    },

    header: {
        // Use static positioning and reset the z-index so that the header
        // doesn't overlap the accordion section's hover/focus outline.
        position: "static",
        zIndex: "auto",
        flexShrink: 0,
        height: 100,
        padding: `${spacing.medium_16}px ${spacing.large_24}px`,

        backgroundColor: color.white,
        border: `1px solid ${color.offBlack16}`,
        borderBottom: "none",
        borderStartStartRadius: spacing.small_12,
        borderStartEndRadius: spacing.small_12,
    },

    sectionHeader: {
        padding: `${spacing.medium_16}px ${spacing.large_24}px`,
    },

    childListItem: {
        display: "flex",
        alignItems: "center",
        minHeight: "48px",
    },

    childListItemSelected: {
        backgroundColor: "rgba(24, 101, 242, 0.08)",
    },

    childListItemLink: {
        flexGrow: 1, // Ensure the entire box is a click target.
        padding: "8px 32px",

        color: color.offBlack,
    },

    childListItemLinkSelected: {
        color: color.blue,
    },
});
